import type { SentimentEventDto, SentimentProviderDto, SentimentTestResponse } from '@et/shared';
import { useState } from 'react';
import { api, describeError } from '../api/client';
import { dateTime, money } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';
import { Coin } from '../components/CoinLogo';

export function Sentiment(): JSX.Element {
  const providers = useResource<SentimentProviderDto[]>('/sentiment/providers');
  const events = useResource<SentimentEventDto[]>('/sentiment/events?limit=100');
  const [text, setText] = useState('Binance Will List Hyperliquid (HYPE)');
  const [result, setResult] = useState<SentimentTestResponse | null>(null);
  const tr = useT();

  const update = async (key: string, body: object): Promise<void> => {
    try {
      providers.setData(await api.put<SentimentProviderDto[]>(`/sentiment/providers/${key}`, body));
      toast.success(t('Saved — applied without restart'));
    } catch (err) {
      toast.error(describeError(err));
    }
  };

  return (
    <div className="stack">
      <h1>{tr('Sentiment')}</h1>
      <div className="card table-wrap">
        <table>
          <thead><tr><th>{tr('Adapter')}</th><th>{tr('Enabled')}</th><th>{tr('Weight')}</th><th>{tr('Health')}</th><th className="num">{tr('Calls today')}</th><th className="num">{tr('Cost today')}</th><th className="num">{tr('Per call')}</th></tr></thead>
          <tbody>{providers.data?.map((p) => (
            <tr key={p.key}>
              <td>{p.key}{p.credentialRequired && <span className="muted"> · {tr('needs key (paid)')}</span>}</td>
              <td><input type="checkbox" checked={p.enabled} onChange={(e) => void update(p.key, { enabled: e.target.checked })} /></td>
              <td><input className="mono" style={{ width: 70 }} defaultValue={p.weight} onBlur={(e) => { if (e.target.value !== p.weight) void update(p.key, { weight: e.target.value }); }} /></td>
              <td>{p.healthy === null ? <span className="muted">—</span> : p.healthy ? <span className="chip good"><span className="dot" /> {tr('ok')}</span> : <span className="chip critical"><span className="dot" /> {tr('failing')}</span>}</td>
              <td className="num">{p.callsToday}</td>
              <td className="num">${money(p.costEstimateUsd, 4)}</td>
              <td className="num">{p.costPerCallUsd === null ? tr('free') : `$${p.costPerCallUsd}`}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="card stack">
        <h2>{tr('Playground')}</h2>
        <div className="row">
          <input style={{ flex: 1 }} value={text} onChange={(e) => { setText(e.target.value); }} />
          <button className="primary" onClick={() => void api.post<SentimentTestResponse>('/sentiment/test', { text }).then(setResult, (err: unknown) => { toast.error(describeError(err)); })}>{tr('Score')}</button>
        </div>
        {result && (result.results.length === 0 ? <div className="muted">{tr('No opinion — no rule matched this headline.')}</div> : (
          <table><tbody>{result.results.map((r) => (
            <tr key={r.adapter}><td>{r.adapter}</td><td className={r.direction === 'bullish' ? 'pos' : r.direction === 'bearish' ? 'neg' : ''}>{tr(r.direction)}</td><td className="num">{tr('score')} {r.score}</td><td className="num">{tr('confidence')} {r.confidence}</td><td className="muted">{r.rationale}</td></tr>
          ))}</tbody></table>
        ))}
      </div>

      <div className="card">
        <h2>{tr('Recent scores')}</h2>
        <div className="table-wrap"><table>
          <thead><tr><th>{tr('Time')}</th><th>{tr('Adapter')}</th><th>{tr('Symbol')}</th><th className="num">{tr('Score')}</th><th className="num">{tr('Conf.')}</th><th>{tr('Headline')}</th></tr></thead>
          <tbody>{events.data?.map((e) => (
            <tr key={e.id}><td className="muted">{dateTime(e.createdAt)}</td><td>{e.adapter}</td><td>{e.symbol ? <Coin symbol={e.symbol} /> : '—'}</td><td className={`num ${e.direction === 'bullish' ? 'pos' : e.direction === 'bearish' ? 'neg' : ''}`}>{e.score}</td><td className="num">{e.confidence}</td><td style={{ whiteSpace: 'normal' }}>{e.newsTitle}</td></tr>
          ))}</tbody>
        </table></div>
        {events.data?.length === 0 && <div className="empty">{tr('No scores yet.')}</div>}
      </div>
    </div>
  );
}
